/* eslint-disable import/default */
import chiaBls from 'chia-bls';
import type { Coin, CoinSpend } from 'chia-rpc';
import clvmLib, { type Program as ProgramType } from 'clvm-lib';
/* eslint-enable import/default */
import { type SpendableCoin, toCoinId } from '~/composables/useGoby';
import {
  AssertCoinAnnouncement,
  CreateCoin,
  CreateCoinAnnouncement,
  ReserveFeeCondition,
  selectXchCoins,
  solutionForConditions,
  trim0x,
} from '~/utilities/chia';
import { createMessageSpend } from '~/utilities/dids';

/* eslint-disable import/no-named-as-default-member */
const { fromHex, toHex } = chiaBls;
const { Program } = clvmLib;
/* eslint-enable import/no-named-as-default-member */

export function setNftDid(spendableNftCoins: SpendableCoin[], spendableDidCoin: SpendableCoin | null): CoinSpend[] {
  const parsedNfts: (SpendableCoin & { nftId: Uint8Array; currentDidId: ProgramType; p2Puzzle: ProgramType })[] = [];
  for (const spendableNftCoin of spendableNftCoins) {
    const nftPuzzle = Program.deserializeHex(spendableNftCoin.puzzle);

    const [, curriedArgs] = nftPuzzle.uncurry()!;
    const nftId = curriedArgs[0].rest.first.toBytes();

    const nftStateLayerPuzzle = curriedArgs[1];
    const [, stateLayerCurriedArgs] = nftStateLayerPuzzle.uncurry()!;

    const nftOwnershipLayerPuzzle = stateLayerCurriedArgs[3];
    const [, ownershipLayerCurriedArgs] = nftOwnershipLayerPuzzle.uncurry()!;

    const currentDidId = ownershipLayerCurriedArgs[1];
    const p2Puzzle = ownershipLayerCurriedArgs[3];

    parsedNfts.push({ ...spendableNftCoin, nftId, currentDidId, p2Puzzle });
  }

  let didId: Uint8Array;
  let didInnerHash: Uint8Array;
  let didCoinSpend: CoinSpend | undefined;
  if (spendableDidCoin !== null) {
    ({ didId, didInnerHash, didCoinSpend } = createMessageSpend(
      spendableDidCoin,
      parsedNfts.map((nft) => nft.nftId)
    ));
  } else {
    didId = Program.fromList([]).toBytes();
    didInnerHash = Program.fromList([]).toBytes();
    didCoinSpend = undefined;
  }

  console.log(`Moving NFTs [${parsedNfts.map((nft) => toHex(nft.nftId)).join(', ')}] to DID ${toHex(didId)}`);

  const coinSpends = [];
  for (const [index, nft] of parsedNfts.entries()) {
    if (didId === nft.currentDidId.toBytes()) {
      console.log('This NFT is already associated with this profile, ignoring.');
      continue;
    }

    if (!nft.lineageProof) {
      throw new Error('Missing lineage proof');
    }

    const conditions = [
      CreateCoin(nft.p2Puzzle.hash(), nft.coin.amount, [nft.p2Puzzle.hash()]),
      Program.fromList([
        Program.fromInt(-10),
        Program.fromBytes(didId),
        Program.fromList([]),
        Program.fromBytes(didInnerHash),
      ]),
    ];

    if (index === 0) {
      conditions.push(CreateCoinAnnouncement(fromHex('0x80')));
    }

    const p2Solution = solutionForConditions(conditions);
    const ownershipLayerSolution = Program.fromList([p2Solution]);
    const stateLayerSolution = Program.fromList([ownershipLayerSolution]);
    const fullSolution = Program.fromList([
      Program.fromList([
        Program.fromHex(trim0x(nft.lineageProof.parentName!)),
        Program.fromHex(trim0x(nft.lineageProof.innerPuzzleHash!)),
        Program.fromInt(nft.lineageProof.amount!),
      ]),
      Program.fromInt(nft.coin.amount),
      stateLayerSolution,
    ]);

    coinSpends.push({
      coin: nft.coin,
      puzzle_reveal: nft.puzzle,
      solution: fullSolution.serializeHex(),
    });
  }
  if (didCoinSpend) {
    coinSpends.push(didCoinSpend);
  }
  return coinSpends;
}

export function createSecureTheBagRootSpend(
  didSpendableCoin: SpendableCoin,
  rootPuzzleHex: string,
  rootCoinAmount: number,
  xchCoins: SpendableCoin[],
  extraCoinsToCreate: { puzzleHash: Uint8Array; amount: number }[] = [],
  fee = 100_000_000
) {
  const secureTheMintRootPuzzle = Program.deserializeHex(rootPuzzleHex);

  const didFullPuzzle = Program.deserializeHex(didSpendableCoin.puzzle);
  const [, curriedArgs] = didFullPuzzle.uncurry()!;
  const innerPuzzle = curriedArgs[1];
  const didInnerHash = innerPuzzle.hash();
  const [, innerCurriedArgs] = innerPuzzle.uncurry()!;
  const p2Puzzle = innerCurriedArgs[0];

  const didCoin = didSpendableCoin.coin;
  const didCoinName = toCoinId(didCoin);
  console.log('DID coin ID', toHex(didCoinName));

  const lineageProof = didSpendableCoin.lineageProof;
  if (!lineageProof || !lineageProof.parentName || !lineageProof.innerPuzzleHash || !lineageProof.amount) {
    throw new Error('Missing lineage proof');
  }
  const p2Solution = solutionForConditions([
    CreateCoin(didInnerHash, didCoin.amount, [p2Puzzle.hash()]),
    CreateCoin(secureTheMintRootPuzzle.hash(), 0),
    CreateCoinAnnouncement(fromHex('24')),
  ]);
  const didInnerSolution = Program.fromList([Program.fromInt(1), p2Solution]);
  const didSolution = Program.fromList([
    Program.fromList([
      Program.fromHex(lineageProof.parentName.replace('0x', '')),
      Program.fromHex(lineageProof.innerPuzzleHash.replace('0x', '')),
      Program.fromInt(lineageProof.amount),
    ]),
    Program.fromInt(didCoin.amount),
    didInnerSolution,
  ]);

  const didCoinSpend: CoinSpend = {
    coin: didCoin,
    puzzle_reveal: didFullPuzzle.serializeHex(),
    solution: didSolution.serializeHex(),
  };

  const stbRootCoin: Coin = {
    amount: 0,
    parent_coin_info: `0x${toHex(didCoinName)}`,
    puzzle_hash: `0x${secureTheMintRootPuzzle.hashHex()}`,
  };

  const stbRootSpend: CoinSpend = {
    coin: stbRootCoin,
    puzzle_reveal: secureTheMintRootPuzzle.serializeHex(),
    solution: Program.fromList([]).serializeHex(),
  };

  const extraCoinAmount = extraCoinsToCreate.reduce((acc, coin) => acc + coin.amount, 0);
  const { selectedCoins: feeCoins, changeAmount } = selectXchCoins(xchCoins, fee + rootCoinAmount + extraCoinAmount);
  const feeCoinSpends = feeCoins.map((feeCoin, index) => {
    const conditions = [AssertCoinAnnouncement(toCoinId(stbRootCoin), fromHex('24'))];
    if (index === 0) {
      conditions.push(
        ...[
          AssertCoinAnnouncement(didCoinName, fromHex('24')),
          ...(changeAmount ? [CreateCoin(fromHex(feeCoin.coin.puzzle_hash.replace('0x', '')), changeAmount)] : []),
          ...(fee ? [ReserveFeeCondition(fee)] : []),
          ...extraCoinsToCreate.map((coin) => CreateCoin(coin.puzzleHash, coin.amount)),
        ]
      );
    }
    const inputCoinSolution = solutionForConditions(conditions);
    return {
      coin: feeCoin.coin,
      puzzle_reveal: feeCoin.puzzle,
      solution: inputCoinSolution.serializeHex(),
    };
  });

  return [didCoinSpend, ...feeCoinSpends, stbRootSpend];
}
